import React, { useMemo, useState } from 'react';
import useSearchStrategies from 'hooks/services/strategy/useSearchStrategies';
import useAddProspectToStrategy from 'hooks/services/strategy/useAddProspectToStrategy';
import { useConditionalSnackBar, useSnackbar } from 'context/snackbar';
import {
  externalPropertiesToTagInsert,
  fullName,
  getError,
  isProspectOwned,
} from 'crono-fe-common/utils';
import { FeConstants } from 'constants/FeConstants';
import { useAnalytics } from 'context/analytics';
import { Either3 } from 'utils/businessLogic';
import { Strategy } from 'crono-fe-common/types/strategy';
import { Button, PlusMdIcon } from '@crono-one/design-system';
import { Prospect } from 'crono-fe-common/types/prospect';
import { useAuth } from 'context/auth';
import StrategiesSortPicker from 'crono-fe-common/components/StrategiesSortPicker';
import { useStrategyFilters } from 'pages/strategy/filters/strategyFiltersContext';
import { List } from 'crono-fe-common/types/list';
import { useCreateNewStrategyNavigation } from 'context/strategyDetail/useCreateNewStrategyNavigation';
import { useInvalidateGetStrategy } from 'hooks/services/strategy/useGetStrategy';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';
import useNonOwnedSequenceActionGuard from 'hooks/strategy/useNonOwnedSequenceActionGuard';
import useGetUsers from 'hooks/services/subscription/useGetUsers';
import { format } from 'date-fns';
import SequencePickerPanel from 'crono-fe-common/components/SequencePicker/SequencePickerPanel';
import SequenceCard from 'crono-fe-common/components/SequencePicker/SequenceCard';
import TagsBoxShowAll from 'components/TagsBoxShowAll';
import SequenceListsFilter from './SequenceListsFilter';
import SequenceTagsFilter from './SequenceTagsFilter';

type IProps = {
  setOpenTooltip?: React.Dispatch<React.SetStateAction<boolean>>;
  //This function is fired when the prospect is correctly added to the sequence
  callOnSuccess?: () => void;
  style?: React.CSSProperties;
  //Off for the selectors that pick a sequence without enrolling anyone, like the
  //conditional step's branch target, where a start date would mean nothing
  enableStartDate?: boolean;
} & Either3<
  {
    prospect: Prospect;
  },
  {
    handleMultipleAdd: (strategy: Strategy, startDate?: string) => void;
  },
  {
    returnSelectedStrategy: (strategy: Strategy, startDate?: string) => void;
  }
>;

const StrategySelector = ({
  callOnSuccess,
  setOpenTooltip,
  style,
  enableStartDate = true,
  ...props
}: IProps) => {
  const { user } = useAuth();
  const { isManager } = useRoleCapabilities();
  const { openNonOwnedSequenceActionModal } = useNonOwnedSequenceActionGuard();
  const [search, setSearch] = useState<string>('');
  const [selectedList, setSelectedList] = useState<List | null>(null);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const analytics = useAnalytics();
  const {
    selectedUser,
    setSelectedUser,
    sortOption,
    setSortOption,
    filterExternalProperties,
    setFilterExternalProperties,
  } = useStrategyFilters();

  const flatTagsFilter = useMemo(() => {
    return externalPropertiesToTagInsert(filterExternalProperties);
  }, [filterExternalProperties]);
  const { data: strategiesDb, isLoading: isSearchStrategiesLoading } =
    useSearchStrategies({
      limit: FeConstants.strategyLimit,
      offset: 0,
      name: search,
      userId: selectedUser === user?.id ? selectedUser : undefined, // since only "my sequences" filter is on the picker, so if other userId is present - ignore it
      strategyTags: flatTagsFilter.length ? flatTagsFilter : undefined,
      filterByTags: !!flatTagsFilter.length,
      sort: sortOption,
      listId: selectedList?.id || null,
    });
  const strategies = strategiesDb?.data?.data ?? [];

  const {
    mutateAsync: addProspectToStrategyAsync,
    error: addProspectToStrategyError,
    isLoading: isAddProspectToStrategyLoading,
  } = useAddProspectToStrategy({
    onSuccess: (result, variables) => {
      callOnSuccess?.();
      setOpenTooltip?.(false);
      invalidateGetStrategy({ strategyId: variables.data.strategyId });
    },
  });

  useConditionalSnackBar([
    {
      condition: !!addProspectToStrategyError,
      message:
        getError(addProspectToStrategyError) ?? 'Error adding to strategy',
      severity: 'error',
    },
  ]);
  const { pushSnackbar } = useSnackbar();

  const invalidateGetStrategy = useInvalidateGetStrategy();

  const handleAddStrategy = async (strategy: Strategy) => {
    //Sent as a plain calendar day rather than an instant: the sequence starts on
    //the day the user picked, with no time zone to shift it either way
    const startDateToSend =
      enableStartDate && startDate
        ? format(startDate, 'yyyy-MM-dd')
        : undefined;

    //If the selector has been opened by the multiple add tooltip
    if ('handleMultipleAdd' in props && props.handleMultipleAdd) {
      props.handleMultipleAdd(strategy, startDateToSend);
    } else if ('prospect' in props && props.prospect) {
      const prospect = props.prospect;

      const enrolProspect = async () => {
        const response = await addProspectToStrategyAsync({
          data: {
            accountId: prospect.accountId,
            prospectId: prospect.objectId,
            strategyId: strategy.id,
            startDate: startDateToSend,
          },
          recipient: prospect,
          strategySettings: strategy.strategySettings ?? null,
        });
        const createdSequenceInstance = response?.data?.data;

        if (
          createdSequenceInstance &&
          createdSequenceInstance.tasks.length > 0
        ) {
          const skippedTasksCount = createdSequenceInstance.tasks.filter(
            (task) => task.skipped === true,
          ).length;
          if (skippedTasksCount > 0) {
            pushSnackbar({
              severity: 'success',
              message: `Contact successfully enrolled in the sequence with ${skippedTasksCount}/${createdSequenceInstance.tasks.length} tasks skipped due to missing or invalid data`,
            });
          }
        }

        if (analytics) {
          analytics.track('add-to-sequence', {
            type: 'add',
          });
        }
      };

      if (isManager && !isProspectOwned(prospect)) {
        openNonOwnedSequenceActionModal('add', enrolProspect);
      } else {
        await enrolProspect();
      }
    } else if (
      'returnSelectedStrategy' in props &&
      props.returnSelectedStrategy
    ) {
      props.returnSelectedStrategy(strategy, startDateToSend);
      if (callOnSuccess) callOnSuccess();
      if (setOpenTooltip) setOpenTooltip(false);
    }
  };

  const { setShowCreateModal } = useCreateNewStrategyNavigation();
  const handleClickNew = () => {
    setShowCreateModal(true);
  };

  const { data: users } = useGetUsers();
  const ownerNameById = useMemo(
    () =>
      new Map(
        (users?.data?.data ?? []).map((u) => [
          u.id,
          fullName(u.firstName, u.lastName),
        ]),
      ),
    [users],
  );

  return (
    <SequencePickerPanel<Strategy>
      style={style}
      search={search}
      onSearchChange={setSearch}
      myOnly={selectedUser === user?.id}
      onMyOnlyChange={(checked) =>
        // undefined means "all users" in new context
        setSelectedUser(checked && user ? user.id : undefined)
      }
      listsFilter={
        <SequenceListsFilter
          selectedList={selectedList}
          setSelectedList={setSelectedList}
        />
      }
      tagsFilter={
        <SequenceTagsFilter
          filterExternalProperties={filterExternalProperties}
          setFilterExternalProperties={setFilterExternalProperties}
        />
      }
      sortPicker={
        <StrategiesSortPicker
          sortOption={sortOption}
          setSortOption={setSortOption}
          greenVersion={true}
          inline
        />
      }
      items={strategies}
      getItemKey={(strategy) => strategy.id}
      renderItem={(strategy) => (
        <SequenceCard
          strategy={strategy}
          currentUserId={user?.id}
          ownerName={
            strategy.userId ? ownerNameById.get(strategy.userId) : null
          }
          tagsTooltip={
            <TagsBoxShowAll
              type="AccountAndProspect"
              blockIsEditable={true}
              disableEdit={true}
              showRemovable={false}
              currentSituationTags={
                strategy.tags?.map((tag) => ({
                  externalPropertyId: tag.externalPropertyId,
                  option: tag.value,
                })) ?? []
              }
            />
          }
          onClick={() => handleAddStrategy(strategy)}
        />
      )}
      isLoading={isAddProspectToStrategyLoading || isSearchStrategiesLoading}
      enableStartDate={enableStartDate}
      startDate={startDate}
      onStartDateChange={setStartDate}
      footerEnd={
        <Button
          variant="solid"
          tone="gray"
          shape="pill"
          size="sm"
          className="pl-2 pr-3"
          onClick={handleClickNew}
        >
          <PlusMdIcon className="shrink-0" />
          New
        </Button>
      }
    />
  );
};

export default StrategySelector;
